/**
 * Iter-411 — Notification Templates editor.
 *
 * Lightweight modal that lists every tenant notification template (custom +
 * defaults) and lets an admin edit / save / delete them.  Reuses the CRUD
 * endpoints already present in routes/school_notifications.py:
 *   GET    /school/tenants/{tid}/notification-templates
 *   PUT    /school/tenants/{tid}/notification-templates/{trigger}
 *   DELETE /school/tenants/{tid}/notification-templates/{trigger}
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Save, Trash2, MessageCircle } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const _token = () => {
  try { const p = sessionStorage.getItem('school_preview_token'); if (p) return p; }
  catch { /* ignore */ }
  return getToken();
};

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: {
      Authorization: `Bearer ${_token()}`,
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const NotificationTemplatesDialog = ({ open, onOpenChange, tenantId, focus }) => {
  const qc = useQueryClient();
  const [trigger, setTrigger] = useState(focus || 'grade_published');
  const [draft, setDraft] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['notification-templates', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/notification-templates`),
    enabled: open && !!tenantId,
  });
  const templates = data?.templates || [];
  const active = useMemo(
    () => templates.find(t => t.trigger === trigger) || null,
    [templates, trigger],
  );

  React.useEffect(() => { setDraft(active ? { ...active } : null); }, [active]);
  React.useEffect(() => { if (open && focus) setTrigger(focus); }, [open, focus]);

  const save = useMutation({
    mutationFn: (payload) => api(
      `/tenants/${tenantId}/notification-templates/${trigger}`,
      { method: 'PUT', body: JSON.stringify(payload) },
    ),
    onSuccess: () => {
      toast.success('Template saved');
      qc.invalidateQueries({ queryKey: ['notification-templates', tenantId] });
    },
    onError: (e) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: () => api(
      `/tenants/${tenantId}/notification-templates/${trigger}`,
      { method: 'DELETE' },
    ),
    onSuccess: () => {
      toast.success('Reverted to default');
      qc.invalidateQueries({ queryKey: ['notification-templates', tenantId] });
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="notification-templates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-indigo-500" />
            Notification templates
          </DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Trigger</Label>
              <Select value={trigger} onValueChange={setTrigger}>
                <SelectTrigger data-testid="template-trigger-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.trigger} value={t.trigger}>
                      {t.name || t.trigger}
                      {t.is_default && ' · (default)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {draft && (
              <>
                <div>
                  <Label className="text-xs">Display name</Label>
                  <Input value={draft.name || ''}
                         onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
                         data-testid="template-name" />
                </div>
                <div>
                  <Label className="text-xs">Channel</Label>
                  <Select value={draft.channel || 'both'}
                          onValueChange={(v) => setDraft(d => ({ ...d, channel: v }))}>
                    <SelectTrigger data-testid="template-channel"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both">SMS + WhatsApp</SelectItem>
                      <SelectItem value="sms">SMS only</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp only</SelectItem>
                      <SelectItem value="email">Email only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Message body</Label>
                  <textarea
                    className="w-full text-sm border rounded p-2 min-h-[120px] font-mono"
                    value={draft.template || ''}
                    onChange={(e) => setDraft(d => ({ ...d, template: e.target.value }))}
                    data-testid="template-body"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Variables: <code>{'{parent_name}'}</code>, <code>{'{student_name}'}</code>,
                    <code>{'{exam_name}'}</code>, <code>{'{score}'}</code>, <code>{'{subject}'}</code>,
                    <code>{'{school_name}'}</code>, <code>{'{link}'}</code>.
                  </p>
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={!!draft.auto_send}
                         onChange={(e) => setDraft(d => ({ ...d, auto_send: e.target.checked }))}
                         data-testid="template-auto-send" />
                  Auto-send when this trigger fires
                </label>
                {draft.is_default && (
                  <Badge className="bg-slate-100 text-slate-600 text-[10px]">
                    Using default — save to create a tenant override
                  </Badge>
                )}
              </>
            )}
          </div>
        )}
        <DialogFooter>
          {active && !active.is_default && (
            <Button variant="outline" onClick={() => del.mutate()}
                    disabled={del.isPending}
                    className="mr-auto text-rose-600"
                    data-testid="template-delete">
              <Trash2 className="h-4 w-4 mr-1" /> Revert to default
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={() => save.mutate({
            trigger,
            name: draft?.name || '',
            template: draft?.template || '',
            channel: draft?.channel || 'both',
            auto_send: !!draft?.auto_send,
          })}
                  disabled={!draft || save.isPending}
                  data-testid="template-save">
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationTemplatesDialog;
